import React, { useCallback, useEffect, useRef, useState } from "react";
import Badge from "react-bootstrap/Badge";
import { MenuItemWithTooltip, MenuSection } from "../../Menu";
import MarketSelector from "./MarketSelector";
import { filterNavigationItems } from "./navigationSearch";

const INITIAL_EXPANDED_GROUPS = {
  marketDataParent: [],
  submitDataParent: [],
};

const areExpandedGroupsEqual = (left = {}, right = {}) =>
  JSON.stringify(left) === JSON.stringify(right);

const createSearchGroupSession = () => ({
  active: false,
  snapshot: {
    marketDataParent: [],
    submitDataParent: [],
  },
  retainedGroups: {},
});

// Shared navigation state hook for search, filtering, rendering helpers, and auto-expansion behavior.
const useNavigationMenu = ({
  expandedMenus,
  startNavigationSearchSession,
  commitNavigationSearchResult,
  endNavigationSearchSession,
  syncAutoExpandedMenus,
  onMenuToggle,
  handleNavLinkClick,
  handleOpenFlexTab,
  handleCollapseOthers,
  activeMenuItems,
}) => {
  const [navigationSearch, setNavigationSearch] = useState("");
  const [expandedGroups, setExpandedGroups] = useState(INITIAL_EXPANDED_GROUPS);
  const navigationSearchGroupsSessionRef = useRef(createSearchGroupSession());

  const isSearching = navigationSearch.trim() !== "";

  useEffect(() => {
    if (isSearching) {
      startNavigationSearchSession();

      if (!navigationSearchGroupsSessionRef.current.active) {
        navigationSearchGroupsSessionRef.current = {
          active: true,
          snapshot: {
            marketDataParent: [...(expandedGroups.marketDataParent || [])],
            submitDataParent: [...(expandedGroups.submitDataParent || [])],
          },
          retainedGroups: {},
        };
      }

      return;
    }

    endNavigationSearchSession();

    if (!navigationSearchGroupsSessionRef.current.active) {
      return;
    }

    const { snapshot, retainedGroups } = navigationSearchGroupsSessionRef.current;
    const restoredExpandedGroups = {
      marketDataParent: Array.from(
        new Set([
          ...(snapshot.marketDataParent || []),
          ...(retainedGroups.marketDataParent || []),
        ])
      ),
      submitDataParent: Array.from(
        new Set([
          ...(snapshot.submitDataParent || []),
          ...(retainedGroups.submitDataParent || []),
        ])
      ),
    };

    navigationSearchGroupsSessionRef.current = createSearchGroupSession();

    setExpandedGroups((prev) =>
      areExpandedGroupsEqual(prev, restoredExpandedGroups)
        ? prev
        : restoredExpandedGroups
    );
  }, [
    endNavigationSearchSession,
    expandedGroups,
    isSearching,
    startNavigationSearchSession,
  ]);

  const handleItemClick = useCallback(
    (customHandler) => (eventOrConfig) => {
      if (customHandler) {
        customHandler(eventOrConfig);
      }

      if (handleNavLinkClick) {
        handleNavLinkClick(eventOrConfig);
      }
    },
    [handleNavLinkClick]
  );

  const renderReviewCountLabel = useCallback((label, count) => {
    if (!(count > 0)) {
      return label;
    }

    return (
      <span className="review-count-label">
        <span className="review-count-label__text">{label}</span>
        <Badge
          pill
          bg="warning"
          text="dark"
          className="review-count-label__badge"
        >
          {count}
        </Badge>
      </span>
    );
  }, []);

  const renderMenuItem = useCallback(
    ({
      to,
      icon,
      text,
      onClick,
      size = "normal",
      id = "",
      component = "",
      name = "",
      config = {},
      isSubmenuItem = false,
      parentIds = [],
      className = "",
      isButton = false,
      searchText = "",
    }) => {
      const isInExpandedSubmenu =
        !isSubmenuItem ||
        (parentIds.length > 0 &&
          parentIds.every((menuKey) => expandedMenus.includes(menuKey)));
      const nestingLevel = parentIds.length;
      const nestingClassName =
        nestingLevel > 0 ? `nesting-level-${nestingLevel}` : "";

      const handleMenuItemClick = (eventOrConfig) => {
        if (isSearching && !isButton) {
          commitNavigationSearchResult({ menuIds: parentIds });

          if (
            config?.searchSessionRestore?.listKey &&
            config?.searchSessionRestore?.expandedGroupId
          ) {
            const { listKey, expandedGroupId } = config.searchSessionRestore;
            const retainedGroups =
              navigationSearchGroupsSessionRef.current.retainedGroups || {};
            const nextListItems = Array.from(
              new Set([...(retainedGroups[listKey] || []), expandedGroupId])
            );

            navigationSearchGroupsSessionRef.current = {
              ...navigationSearchGroupsSessionRef.current,
              retainedGroups: {
                ...retainedGroups,
                [listKey]: nextListItems,
              },
            };
          }
        }

        if (onClick) {
          onClick(eventOrConfig);
        }
      };

      return (
        <MenuItemWithTooltip
          to={to}
          icon={icon}
          text={text}
          onClick={handleMenuItemClick}
          size={size}
          component={component}
          id={id}
          key={`${id}-${typeof text === "string" ? text : name}`}
          name={name}
          isSubmenuItem={isSubmenuItem}
          isExpanded={true}
          parentIds={parentIds}
          onCollapseOthers={handleCollapseOthers}
          className={`${className} ${nestingClassName}`.trim()}
          activeMenuItems={activeMenuItems}
          isButton={isButton}
          isInExpandedSubmenu={isInExpandedSubmenu}
          config={config}
          searchText={searchText}
          showTooltip={false}
        />
      );
    },
    [
      activeMenuItems,
      commitNavigationSearchResult,
      expandedMenus,
      handleCollapseOthers,
      isSearching,
    ]
  );

  const renderMenuSection = useCallback(
    (title, icon, children, menuId = "", parentIds = []) => {
      const newParentIds = menuId ? [...parentIds, menuId] : [...parentIds];

      return (
        <MenuSection
          title={title}
          icon={icon}
          isExpanded={expandedMenus.includes(menuId)}
          onToggle={onMenuToggle}
          menuId={menuId}
          parentIds={parentIds}
          onCollapseOthers={handleCollapseOthers}
          activeMenuItems={activeMenuItems}
        >
          {typeof children === "function" ? children(newParentIds) : children}
        </MenuSection>
      );
    },
    [activeMenuItems, expandedMenus, handleCollapseOthers, onMenuToggle]
  );

  const renderNavigationDescriptor = useCallback(
    (item) => {
      if (!item) {
        return null;
      }

      if (item.kind === "submenu") {
        return React.cloneElement(
          renderMenuSection(
            item.label,
            item.icon,
            <>
              {item.groups?.map((group, index) => (
                <React.Fragment
                  key={`${item.id}-${group.label || "default"}-${index}`}
                >
                  {group.label ? (
                    <div className="sidebar-nav-group-label">{group.label}</div>
                  ) : null}
                  {(group.items || []).map(renderNavigationDescriptor)}
                </React.Fragment>
              ))}
            </>,
            item.id,
            item.parentIds || []
          ),
          { key: item.id }
        );
      }

      if (item.kind === "marketSelector") {
        const requiredExpandedParentId =
          item.requiresExpandedParent && Array.isArray(item.parentIds)
            ? item.parentIds[item.parentIds.length - 1]
            : null;

        if (
          requiredExpandedParentId &&
          !isSearching &&
          !expandedMenus.includes(requiredExpandedParentId)
        ) {
          return null;
        }

        return (
          <MarketSelector
            key={item.id || item.listKey}
            markets={item.markets}
            expandedGroups={expandedGroups}
            setExpandedGroups={setExpandedGroups}
            listKey={item.listKey}
            component={item.component}
            routePrefix={item.routePrefix}
            namePrefix={item.namePrefix}
            parentIds={item.parentIds}
            renderMenuItem={renderMenuItem}
            manageExternalMarkets={item.manageExternalMarkets}
            onItemClick={handleItemClick(handleOpenFlexTab)}
            activeMenuItems={activeMenuItems}
            enableSearch={item.enableSearch && !isSearching}
            forceExpandAll={item.forceExpandAll}
            autoExpandedProductIds={item.autoExpandedProductIds}
            navigationSearchValue={item.navigationSearchValue}
            termSchedules={item.termSchedules}
          />
        );
      }

      return renderMenuItem({
        to: item.to,
        icon: item.icon,
        text:
          typeof item.badgeCount === "number"
            ? renderReviewCountLabel(item.label, item.badgeCount)
            : item.label,
        onClick:
          item.kind === "button"
            ? item.onActivate
            : handleItemClick(handleOpenFlexTab),
        size: item.size,
        id: item.id,
        component: item.component,
        name: item.name,
        config: item.config,
        isSubmenuItem: item.isSubmenuItem,
        parentIds: item.parentIds,
        className: item.className,
        isButton: item.kind === "button",
        searchText: item.searchText || item.label,
      });
    },
    [
      activeMenuItems,
      expandedMenus,
      expandedGroups,
      handleItemClick,
      handleOpenFlexTab,
      isSearching,
      renderMenuItem,
      renderMenuSection,
      renderReviewCountLabel,
    ]
  );

  const filterNavigationDescriptors = useCallback(
    (items) =>
      filterNavigationItems({
        items,
        isSearching,
        searchValue: navigationSearch,
      }),
    [isSearching, navigationSearch]
  );

  const filterNavigationGroups = useCallback(
    (groups = []) => {
      const nextAutoExpandedMenuIds = [];
      const filteredGroups = groups
        .map((group) => {
          const filteredGroup = filterNavigationDescriptors(group.items || []);

          nextAutoExpandedMenuIds.push(...filteredGroup.autoExpandedMenuIds);

          return {
            ...group,
            items: filteredGroup.items,
          };
        })
        .filter((group) => group.items.length > 0);

      return {
        groups: filteredGroups,
        autoExpandedMenuIds: Array.from(new Set(nextAutoExpandedMenuIds)),
      };
    },
    [filterNavigationDescriptors]
  );

  const syncAutoExpandedMenuIds = useCallback(
    (menuIds) => {
      syncAutoExpandedMenus(menuIds);
    },
    [syncAutoExpandedMenus]
  );

  return {
    navigationSearch,
    setNavigationSearch,
    isSearching,
    renderNavigationDescriptor,
    filterNavigationDescriptors,
    filterNavigationGroups,
    syncAutoExpandedMenuIds,
  };
};

export default useNavigationMenu;
